/**
 * Notification Center System
 * Sistema de notificaciones centralizado con almacenamiento persistente
 */

(function() {
    'use strict';

    const STORAGE_KEY = 'tfx_notifications';
    const SESSION_KEY = 'tfx_notifications_shown_session';
    const MAX_NOTIFICATIONS = 50;

    // Estado global
    let notifications = [];
    let currentTab = 'unread';

    // ========== INITIALIZATION ==========
    function init() {
        loadNotifications();
        setupEventListeners();
        checkSubscriptionExpiry();
        updateBadge();
    }

    // ========== STORAGE ==========
    function loadNotifications() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            notifications = stored ? JSON.parse(stored) : [];
            
            // Limpiar notificaciones muy antiguas (más de 30 días)
            const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
            notifications = notifications.filter(n => n.timestamp > thirtyDaysAgo);
            
            saveNotifications();
        } catch (e) {

            notifications = [];
        }
    }

    function saveNotifications() {
        try {
            // Mantener solo las últimas MAX_NOTIFICATIONS
            if (notifications.length > MAX_NOTIFICATIONS) {
                notifications = notifications.slice(-MAX_NOTIFICATIONS);
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
        } catch (e) {

        }
    }

    // ========== NOTIFICATION MANAGEMENT ==========
    function addNotification(notification) {
        const now = Date.now();
        const type = (notification?.type || '').toString();
        const title = (notification?.title || '').toString();
        const message = (notification?.message || '').toString();
        const dedupeKey = (notification?.dedupeKey || `${type}|${title}|${message}`).toLowerCase();
        const DEDUPE_WINDOW_MS = 60 * 1000;

        const alreadyExists = notifications.some(n => {
            const nType = (n?.type || '').toString();
            const nTitle = (n?.title || '').toString();
            const nMessage = (n?.message || '').toString();
            const nKey = (n?.dedupeKey || `${nType}|${nTitle}|${nMessage}`).toLowerCase();
            return nKey === dedupeKey && Math.abs((n?.timestamp || 0) - now) < DEDUPE_WINDOW_MS;
        });

        if (alreadyExists) {
            return null;
        }

        const newNotification = {
            id: now + Math.random(),
            timestamp: now,
            read: false,
            dedupeKey,
            ...notification
        };

        notifications.push(newNotification);
        saveNotifications();
        updateBadge();
        renderNotifications();

        return newNotification.id;
    }

    function markAsRead(id) {
        const notification = notifications.find(n => n.id === id);
        if (notification) {
            notification.read = true;
            saveNotifications();
            updateBadge();
            renderNotifications();
        }
    }

    function markAllAsRead() {
        notifications.forEach(n => n.read = true);
        saveNotifications();
        updateBadge();
        renderNotifications();
    }

    function deleteNotification(id) {
        notifications = notifications.filter(n => n.id !== id);
        saveNotifications();
        updateBadge();
        renderNotifications();
    }

    // ========== UI ==========
    function setupEventListeners() {
        const notificationBtn = document.getElementById('notificationBtn');
        const closeBtn = document.getElementById('closeNotificationsPanel');
        const markAllReadBtn = document.getElementById('markAllRead');
        const tabs = document.querySelectorAll('.notification-tab');

        if (!notificationBtn) {
            setTimeout(setupEventListeners, 500);
            return;
        }

        notificationBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePanel();
        });

        closeBtn?.addEventListener('click', closePanel);
        markAllReadBtn?.addEventListener('click', () => {
            markAllAsRead();
            if (window.showNotification) {
                window.showNotification('Todas las notificaciones marcadas como leídas', 'success', 2000);
            }
        });

        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                currentTab = tab.dataset.tab;
                updateTabs();
                renderNotifications();
            });
        });

        // Cerrar al hacer clic fuera
        document.addEventListener('click', (e) => {
            const panel = document.getElementById('notificationsPanel');
            const btn = document.getElementById('notificationBtn');
            if (panel && !panel.contains(e.target) && !btn.contains(e.target)) {
                closePanel();
            }
        });
    }

    function togglePanel() {
        const panel = document.getElementById('notificationsPanel');
        const isHidden = panel.style.display === 'none' || panel.classList.contains('hidden');
        if (isHidden) {
            openPanel();
        } else {
            closePanel();
        }
    }

    function openPanel() {
        const panel = document.getElementById('notificationsPanel');
        const btn = document.getElementById('notificationBtn');
        
        panel.style.display = 'block';
        panel.classList.remove('hidden');
        panel.style.visibility = 'hidden';
        panel.style.right = 'auto';
        panel.style.left = '0px';
        panel.style.top = '0px';

        // Posicionar debajo del botón de la campana
        if (btn && panel) {
            const btnRect = btn.getBoundingClientRect();
            const panelRect = panel.getBoundingClientRect();
            const margin = 8;

            let left = btnRect.right - panelRect.width;
            let top = btnRect.bottom + margin;

            const maxLeft = window.innerWidth - panelRect.width - margin;
            const minLeft = margin;

            if (left > maxLeft) left = maxLeft;
            if (left < minLeft) left = minLeft;

            // Evitar que se salga por abajo
            const maxTop = window.innerHeight - panelRect.height - margin;
            if (top > maxTop) top = Math.max(margin, btnRect.top - panelRect.height - margin);

            panel.style.left = `${Math.round(left)}px`;
            panel.style.top = `${Math.round(top)}px`;
        }

        panel.style.visibility = 'visible';

        setTimeout(() => {
            panel.style.opacity = '1';
            panel.classList.remove('opacity-0');
            panel.classList.add('opacity-100');
        }, 10);
        
        renderNotifications();
    }

    function closePanel() {
        const panel = document.getElementById('notificationsPanel');
        
        panel.style.opacity = '0';
        panel.classList.add('opacity-0');
        panel.classList.remove('opacity-100');
        
        setTimeout(() => {
            panel.style.display = 'none';
            panel.classList.add('hidden');
        }, 300);
    }

    function updateTabs() {
        const tabs = document.querySelectorAll('.notification-tab');
        tabs.forEach(tab => {
            const underline = tab.querySelector('span');
            if (tab.dataset.tab === currentTab) {
                tab.classList.remove('text-slate-400', 'hover:text-slate-300');
                tab.classList.add('text-white');
                if (underline) underline.classList.add('scale-x-100');
                if (underline) underline.classList.remove('scale-x-0');
            } else {
                tab.classList.add('text-slate-400', 'hover:text-slate-300');
                tab.classList.remove('text-white');
                if (underline) underline.classList.remove('scale-x-100');
                if (underline) underline.classList.add('scale-x-0');
            }
        });
    }

    function updateBadge() {
        const unreadCount = notifications.filter(n => !n.read).length;
        const badge = document.getElementById('notificationBadge');
        const count = document.getElementById('notificationCount');
        const unreadCountSpan = document.getElementById('unreadCount');

        if (unreadCount > 0) {
            badge?.classList.remove('hidden');
            count?.classList.remove('hidden');
            if (count) count.textContent = unreadCount;
            if (unreadCountSpan) unreadCountSpan.textContent = `(${unreadCount})`;
        } else {
            badge?.classList.add('hidden');
            count?.classList.add('hidden');
            if (unreadCountSpan) unreadCountSpan.textContent = '';
        }
    }

    function renderNotifications() {
        const container = document.getElementById('notificationsList');
        if (!container) {

            return;
        }

        let filtered = currentTab === 'unread' 
            ? notifications.filter(n => !n.read)
            : notifications;

        // Ordenar por timestamp descendente (más reciente primero)
        filtered = filtered.sort((a, b) => b.timestamp - a.timestamp);

        container.innerHTML = filtered.map(n => createNotificationHTML(n)).join('');

        // Agregar event listeners
        filtered.forEach(n => {
            const item = container.querySelector(`[data-notification-id="${n.id}"]`);
            if (item) {
                item.querySelector('.notification-item-content')?.addEventListener('click', () => {
                    markAsRead(n.id);
                    if (n.action) n.action();
                });

                item.querySelector('.delete-notification')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    deleteNotification(n.id);
                });
            }
        });
    }

    function createNotificationHTML(notification) {
        const timeAgo = getTimeAgo(notification.timestamp);
        const iconColor = getIconColor(notification.type);
        const icon = getIcon(notification.type);

        return `
            <div class="notification-item ${notification.read ? 'bg-slate-800/30' : 'bg-slate-800/70'} hover:bg-slate-800 transition-colors border-b border-slate-700/50 cursor-pointer" data-notification-id="${notification.id}">
                <div class="p-3 sm:p-4 flex gap-2 sm:gap-3 notification-item-content">
                    <div class="flex-shrink-0">
                        <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full ${iconColor} flex items-center justify-center">
                            ${icon}
                        </div>
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="flex items-start justify-between gap-2">
                            <p class="text-xs sm:text-sm font-medium text-white break-words">${notification.title}</p>
                            ${!notification.read ? '<span class="flex-shrink-0 w-2 h-2 bg-cyan-400 rounded-full"></span>' : ''}
                        </div>
                        <p class="text-xs sm:text-sm text-slate-400 mt-1 break-words">${notification.message}</p>
                        <div class="flex items-center justify-between mt-2">
                            <span class="text-[10px] sm:text-xs text-slate-500">${timeAgo}</span>
                            <button class="delete-notification text-slate-500 hover:text-red-400 transition-colors p-1">
                                <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    function getIcon(type) {
        const icons = {
            warning: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>',
            info: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
            success: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
            plan: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
        };
        return icons[type] || icons.info;
    }

    function getIconColor(type) {
        const colors = {
            warning: 'bg-orange-500/20 text-orange-400',
            info: 'bg-blue-500/20 text-blue-400',
            success: 'bg-green-500/20 text-green-400',
            plan: 'bg-purple-500/20 text-purple-400'
        };
        return colors[type] || colors.info;
    }

    function getTimeAgo(timestamp) {
        const seconds = Math.floor((Date.now() - timestamp) / 1000);
        
        if (seconds < 60) return 'Ahora';
        if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)}m`;
        if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)}h`;
        if (seconds < 604800) return `Hace ${Math.floor(seconds / 86400)}d`;
        
        return new Date(timestamp).toLocaleDateString('es-ES', { 
            day: 'numeric', 
            month: 'short' 
        });
    }

    // ========== SUBSCRIPTION CHECK ==========
    function checkSubscriptionExpiry() {
        try {
            const userPlan = localStorage.getItem('userPlan');
            const expiresAt = localStorage.getItem('expiresAt');
            const sessionShown = sessionStorage.getItem(SESSION_KEY);

            // Solo para planes PRO/UNLIMITED que expiran
            if (!expiresAt || (userPlan !== 'pro' && userPlan !== 'unlimited')) {
                return;
            }

            const expiryDate = new Date(expiresAt);
            const now = new Date();
            const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

            // Si ya se mostró en esta sesión, no mostrar de nuevo
            if (sessionShown === expiresAt) {
                return;
            }

            // Mostrar si quedan 3 días o menos
            if (daysLeft > 0 && daysLeft <= 3) {
                const notifId = addNotification({
                    type: 'warning',
                    title: '¡Tu suscripción está por expirar!',
                    message: `Te quedan ${daysLeft} día${daysLeft > 1 ? 's' : ''} para renovar y mantener acceso completo.`,
                    action: () => {
                        window.location.href = '/perfil?tab=billing';
                    }
                });

                // Marcar como mostrado en esta sesión
                sessionStorage.setItem(SESSION_KEY, expiresAt);

                // Nota UX: no auto-abrir el panel (se mostraba "mal" en Perfil).
            }
        } catch (e) {

        }
    }

    // ========== PUBLIC API ==========
    window.NotificationCenter = {
        add: addNotification,
        markAsRead,
        markAllAsRead,
        delete: deleteNotification,
        refresh: renderNotifications,
        updateBadge,
        open: openPanel,
        close: closePanel,
        // Debug functions
        test: () => {

            const id = addNotification({
                type: 'info',
                title: 'Test Notification',
                message: 'This is a test notification from the console'
            });
            setTimeout(() => openPanel(), 500);
            return id;
        },
        getAll: () => notifications,
        clear: () => {
            notifications = [];
            saveNotifications();
            updateBadge();
            renderNotifications();
        },
        
        checkFirstLoginOfDay: () => {
            checkFirstLoginOfDay();
        }
    };

    // ========== FIRST LOGIN OF DAY CHECK ==========
    function checkFirstLoginOfDay() {
        try {
            const lastLoginDate = localStorage.getItem('tfx_last_login_date');
            const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            const userName = localStorage.getItem('userName') || 'Usuario';
            const userPlan = (localStorage.getItem('userPlan') || 'FREE').toUpperCase();

            // Si no hay última fecha de login O es diferente a hoy, es la primera sesión del día
            if (lastLoginDate !== today) {
                // Guardar la fecha de hoy
                localStorage.setItem('tfx_last_login_date', today);

                // Crear notificación de bienvenida
                addNotification({
                    type: 'success',
                    title: `¡Bienvenido, ${userName}!`,
                    message: `Plan ${userPlan} - Tienes acceso completo a todas las funciones hoy.`,
                    action: () => {
                        // Redirigir a journal o mantener la página actual
                        window.location.hash = 'welcome';
                    }
                });

                // Nota UX: no auto-abrir el panel (solo incrementa badge / lista).

                return true;
            }
            return false;
        } catch (e) {
            return false;
        }
    }

    // Auto-init cuando el DOM esté listo
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Exponer API global
    window.NotificationCenter = {
        add: addNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clear: () => {
            notifications = [];
            saveNotifications();
            updateBadge();
            renderNotifications();
        },
        
        checkFirstLoginOfDay: () => {
            checkFirstLoginOfDay();
        },
        
        openPanel: openPanel,
        closePanel: closePanel
    };
})();
